/* eslint-disable import/prefer-default-export */

import type { InstrumentType } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { JobType } from '../../../../entities';

export const STEPS = {
  CHOOSE_INSTRUMENT: 'CHOOSE_INSTRUMENT',
  CHOOSE_INSTRUMENT_MODEL: 'CHOOSE_INSTRUMENT_MODEL',
  CHOOSE_INSTRUMENT_MODEL_GUITAR_SUMMIT: 'CHOOSE_INSTRUMENT_MODEL_GUITAR_SUMMIT',
  SOLO: 'SOLO',
  SONG_CRITERIA: 'SONG_CRITERIA',
  DIFFICULTY: 'DIFFICULTY',
  ADVANCED: 'ADVANCED',
  COMPOSER_TITLE: 'COMPOSER_TITLE',
  PEDAL: 'PEDAL',
  LYRICS: 'LYRICS',
  PIANO_STYLE: 'PIANO_STYLE',
  GUITAR_STYLE: 'GUITAR_STYLE',
  TUNING: 'TUNING',
  CAPO: 'CAPO',
  DRUMSET: 'DRUMSET',
  INSTRUMENT_ORDER: 'INSTRUMENT_ORDER',
  SPECIFY_INSTRUMENT: 'SPECIFY_INSTRUMENT',
} as const;

export type Step = keyof typeof STEPS;

export const STEP_TITLE: Record<Step, string> = {
  [STEPS.CHOOSE_INSTRUMENT]: 'home.input.dialog.choose_instrument.title',
  [STEPS.CHOOSE_INSTRUMENT_MODEL]: 'home.input.dialog.choose_instrument.title',
  [STEPS.CHOOSE_INSTRUMENT_MODEL_GUITAR_SUMMIT]: 'home.input.dialog.choose_instrument.title',
  [STEPS.SOLO]: 'home.input.dialog.soloRecording.title',
  [STEPS.SONG_CRITERIA]: 'home.input.dialog.transcription_mode.title',
  [STEPS.DIFFICULTY]: 'home.input.dialog.difficulty.title',
  [STEPS.ADVANCED]: 'home.input.dialog.advanced.title',
  [STEPS.COMPOSER_TITLE]: 'home.input.dialog.title_composer.title',
  [STEPS.PEDAL]: 'home.input.dialog.pedal.title',
  [STEPS.PIANO_STYLE]: 'home.input.dialog.style_piano.title',
  [STEPS.GUITAR_STYLE]: 'home.input.dialog.style_guitar.title',
  [STEPS.CAPO]: 'home.input.dialog.capo.title',
  [STEPS.TUNING]: 'home.input.dialog.tuning.title',
  [STEPS.LYRICS]: 'home.input.dialog.lyrics.title',
  [STEPS.DRUMSET]: 'home.input.dialog.drumset.title',
  [STEPS.INSTRUMENT_ORDER]: 'home.input.dialog.instrument_order.title',
  [STEPS.SPECIFY_INSTRUMENT]: 'home.input.dialog.specify_instrument.title',
};

export const HELPER_TEXT: Record<Step, string> = {
  [STEPS.CHOOSE_INSTRUMENT]: 'home.input.dialog.choose_instrument.help',
  [STEPS.CHOOSE_INSTRUMENT_MODEL]: 'home.input.dialog.choose_instrument.help',
  [STEPS.CHOOSE_INSTRUMENT_MODEL_GUITAR_SUMMIT]: 'home.input.dialog.choose_instrument.help',
  [STEPS.SONG_CRITERIA]: 'home.input.dialog.transcription_mode.help',
  [STEPS.DIFFICULTY]: 'home.input.dialog.difficulty.help',
  [STEPS.SOLO]: 'home.input.dialog.soloRecording.help',
  [STEPS.ADVANCED]: 'home.input.dialog.advanced.help',
  [STEPS.COMPOSER_TITLE]: 'home.input.dialog.title_composer.help',
  [STEPS.PEDAL]: 'home.input.dialog.pedal.help',
  [STEPS.PIANO_STYLE]: 'home.input.dialog.style_piano.help',
  [STEPS.GUITAR_STYLE]: 'home.input.dialog.style_guitar.help',
  [STEPS.CAPO]: 'home.input.dialog.capo.help',
  [STEPS.TUNING]: 'home.input.dialog.tuning.help',
  [STEPS.LYRICS]: 'home.input.dialog.lyrics.help',
  [STEPS.DRUMSET]: 'home.input.dialog.drumset.help',
  [STEPS.INSTRUMENT_ORDER]: 'home.input.dialog.instrument_order.help',
  [STEPS.SPECIFY_INSTRUMENT]: 'home.input.dialog.specify_instrument.help',
};

export const windInstruments = {
  alto_sax: 'home.input.dialog.specify_instrument.instruments.alto_sax',
  tenor_sax: 'home.input.dialog.specify_instrument.instruments.tenor_sax',
  soprano_sax: 'home.input.dialog.specify_instrument.instruments.soprano_sax',
  baritone_sax: 'home.input.dialog.specify_instrument.instruments.baritone_sax',
  clarinet: 'home.input.dialog.specify_instrument.instruments.clarinet',
  flute: 'home.input.dialog.specify_instrument.instruments.flute',
  recorder: 'home.input.dialog.specify_instrument.instruments.recorder',
  oboe: 'home.input.dialog.specify_instrument.instruments.oboe',
  bassoon: 'home.input.dialog.specify_instrument.instruments.bassoon',
  french_horn: 'home.input.dialog.specify_instrument.instruments.french_horn',
  trumpet: 'home.input.dialog.specify_instrument.instruments.trumpet',
  trombone: 'home.input.dialog.specify_instrument.instruments.trombone',
  tuba: 'home.input.dialog.specify_instrument.instruments.tuba',
};

export const stringInstruments = {
  violin: 'home.input.dialog.specify_instrument.instruments.violin',
  viola: 'home.input.dialog.specify_instrument.instruments.viola',
  cello: 'home.input.dialog.specify_instrument.instruments.cello',
  double_bass: 'home.input.dialog.specify_instrument.instruments.double_bass',
};

export interface WizardCtx {
  instruments: InstrumentType[];
  model?: 'universal' | 'rock' | 'classic';
  solo: boolean;
  isRetranscribe?: boolean;
  jobType?: JobType;
  arrangement?: boolean;
}

export type Guard = (ctx: WizardCtx) => boolean;

export const oneInstrumentSelected: Guard = (c) => c.instruments.length === 1;

export const noInstrumentSelected: Guard = (c) => c.instruments.length === 0;

export const multipleInstrumentsSelected: Guard = (c) => c.instruments.length > 1;

export const isPiano: Guard = (c) => c.instruments[0] === 'piano';

export const hasVocals: Guard = (c) => c.instruments.includes('vocals');

export const isSolo: Guard = (c) => c.solo === true;

export const isArrangement: Guard = (c) => c.arrangement === true;

export const hasGuitarStyle: Guard = (c) =>
  (c.instruments[0] === 'guitar' ||
    c.instruments[0] === 'acoustic_guitar' ||
    c.instruments[0] === 'electric_guitar') &&
  isSolo(c);

export const hasCapo: Guard = (c) =>
  c.instruments.includes('guitar') ||
  c.instruments.includes('acoustic_guitar') ||
  c.instruments.includes('electric_guitar');
export const isDrums: Guard = (c) => c.instruments[0] === 'drums';

export const hasTuning: Guard = (c) =>
  c.instruments.includes('guitar') ||
  c.instruments.includes('bass') ||
  c.instruments.includes('acoustic_guitar') ||
  c.instruments.includes('electric_guitar');

export const hasInstrumentOrder: Guard = (c) => c.jobType === JobType.TranscribeScan;

export const isWindInstrument: Guard = (c) =>
  Object.keys(windInstruments).includes(c.instruments[0]) || c.instruments[0] === 'wind';

export const isStringInstrument: Guard = (c) =>
  Object.keys(stringInstruments).includes(c.instruments[0]) || c.instruments[0] === 'strings';

export const shouldSpecifyInstrument: Guard = (c) => {
  return c.model !== 'classic' && (isWindInstrument(c) || isStringInstrument(c));
};

export const COMPOSER_BLACKLIST = ['Bodo Wartke'];
